import * as React from 'react';

import {
  CurrencyCode,
  enumUnit,
  Unit,
} from '@rematter/frontend-lib-api-client';
import { shortenUnit } from '@rematter/ui-web/lib';
import { useAtomValue, useSetAtom } from 'jotai';
import { v4 as uuidv4 } from 'uuid';

import {
  enumLineItemKind,
  type ItemsListEntry,
  NormalizedLineItem,
} from '../../_shared/ItemsInterface/_types';

import { $inventoryItems } from '~/modules/trade/_state/loadForm.state';
import {
  $materialsLocalEntriesById,
  $setMaterialsDirty,
  $setMaterialsLocalEntries,
  type SaveMaterialsParams,
} from '~/modules/trade/_state/materialsForm.state';
import {
  $lastFocusedRowId,
  $lastFocusedWeightFieldType,
  $setAutoFocusRowId,
} from '~/modules/trade/_state/materialsInterface.state';
import { enumLoadWeightType } from '~/modules/trade/types/load';
import { toWeight, zeroWeight } from '~/modules/trade/utils/weight';

type UseUStudioTicketControllerOptions = {
  ticketId: string | null;
  currencyCode: string;
  saveMaterials: (params: SaveMaterialsParams) => void;
  failedToSaveMessage: SaveMaterialsParams['failedToSaveMessage'];
};

const UNIT_OPTIONS = Object.values(enumUnit).map((u) => ({
  value: u,
  label: shortenUnit(u),
}));

export function useUStudioTicketController({
  ticketId,
  currencyCode,
  saveMaterials,
  failedToSaveMessage,
}: UseUStudioTicketControllerOptions) {
  const { data: inventoryItemsData } = useAtomValue($inventoryItems);
  const materialsEntriesById = useAtomValue($materialsLocalEntriesById);
  const setLocalEntries = useSetAtom($setMaterialsLocalEntries);
  const setDirty = useSetAtom($setMaterialsDirty);
  const setAutoFocusRowId = useSetAtom($setAutoFocusRowId);
  const setLastFocusedRowId = useSetAtom($lastFocusedRowId);
  const setLastFocusedWeightFieldType = useSetAtom($lastFocusedWeightFieldType);

  const [currentLineItemId, setCurrentLineItemId] = React.useState<
    string | null
  >(null);

  const quickAddItems = React.useMemo(() => {
    if (!inventoryItemsData?.allItems) return [];
    return inventoryItemsData.allItems;
  }, [inventoryItemsData]);

  const save = React.useCallback(
    (id: string) => saveMaterials({ ticketId: id, failedToSaveMessage }),
    [saveMaterials, failedToSaveMessage],
  );

  const onAddItem = React.useCallback(
    (item: { id: string; effectivePricingUnit?: string }) => {
      if (!ticketId) return;
      const newItemId = `temp-${uuidv4()}`;
      const unit = (item.effectivePricingUnit as Unit) ?? enumUnit.Pounds;
      const newItem: NormalizedLineItem = {
        kind: enumLineItemKind.LineItem,
        id: newItemId,
        type: 'Raw',
        itemId: item.id,
        tagId: null,
        weights: {
          [enumLoadWeightType.Gross]: zeroWeight(),
          [enumLoadWeightType.Tare]: zeroWeight(),
          [enumLoadWeightType.Net]: zeroWeight(),
        },
        unitPrice: null,
        pricingUnit: unit,
      };
      const currentEntries = materialsEntriesById.get(ticketId) ?? [];
      setLocalEntries({ ticketId, entries: [...currentEntries, newItem] });
      setDirty({ ticketId, dirty: true });
      save(ticketId);
      setAutoFocusRowId(newItemId);
      setCurrentLineItemId(newItemId);
    },
    [
      ticketId,
      materialsEntriesById,
      setLocalEntries,
      setDirty,
      save,
      setAutoFocusRowId,
    ],
  );

  const onAddDeduction = React.useCallback(() => undefined, []);

  const onFocusField = React.useCallback(
    (
      lineItemId: string,
      field: 'gross' | 'tare' | 'unitPrice' | 'unit' | 'material',
    ) => {
      setAutoFocusRowId(lineItemId);
      if (field === 'gross' || field === 'tare') {
        setLastFocusedRowId(lineItemId);
        setLastFocusedWeightFieldType(field);
      }
    },
    [setAutoFocusRowId, setLastFocusedRowId, setLastFocusedWeightFieldType],
  );

  const onSetFieldValue = React.useCallback(
    (lineItemId: string, field: string, value: string) => {
      if (!ticketId) return;
      const currentEntries = materialsEntriesById.get(ticketId) ?? [];
      const updated = currentEntries.map((entry): ItemsListEntry => {
        if (entry.kind !== enumLineItemKind.LineItem || entry.id !== lineItemId)
          return entry;
        const item = { ...(entry as NormalizedLineItem) };
        const numValue = parseFloat(value) || 0;
        if (field === 'unitPrice') {
          item.unitPrice = {
            amount: numValue,
            currencyCode: (currencyCode ?? 'USD') as CurrencyCode,
            __typename: 'Money' as const,
          };
          return item;
        }
        const weightType =
          field === 'gross'
            ? enumLoadWeightType.Gross
            : enumLoadWeightType.Tare;
        const otherType =
          field === 'gross'
            ? enumLoadWeightType.Tare
            : enumLoadWeightType.Gross;
        const storedUnit =
          (item.weights?.[weightType]?.unit as Unit) ?? enumUnit.Pounds;
        const otherValue = item.weights?.[otherType]?.value ?? 0;
        const netValue =
          field === 'gross' ? numValue - otherValue : otherValue - numValue;
        item.weights = {
          ...item.weights,
          [weightType]: toWeight(numValue, storedUnit),
          [enumLoadWeightType.Net]: toWeight(netValue, storedUnit),
        };
        return item;
      });
      setLocalEntries({ ticketId, entries: updated });
      setDirty({ ticketId, dirty: true });
      save(ticketId);
    },
    [
      ticketId,
      materialsEntriesById,
      setLocalEntries,
      setDirty,
      save,
      currencyCode,
    ],
  );

  const onSetUnit = React.useCallback(
    (lineItemId: string, unit: string) => {
      if (!ticketId) return;
      const currentEntries = materialsEntriesById.get(ticketId) ?? [];
      const updated = currentEntries.map((entry) => {
        if (entry.kind !== enumLineItemKind.LineItem || entry.id !== lineItemId)
          return entry;
        return { ...entry, pricingUnit: unit as Unit };
      });
      setLocalEntries({ ticketId, entries: updated });
      setDirty({ ticketId, dirty: true });
      save(ticketId);
    },
    [ticketId, materialsEntriesById, setLocalEntries, setDirty, save],
  );

  const onTakeWeight = React.useCallback(
    (lineItemId: string, field: 'gross' | 'tare') => {
      setLastFocusedRowId(lineItemId);
      setLastFocusedWeightFieldType(field);
      setAutoFocusRowId(lineItemId);
    },
    [setLastFocusedRowId, setLastFocusedWeightFieldType, setAutoFocusRowId],
  );

  return {
    quickAddItems,
    availableUnits: UNIT_OPTIONS,
    currentLineItemId,
    onAddItem,
    onAddDeduction,
    onFocusField,
    onSetFieldValue,
    onSetUnit,
    onTakeWeight,
  };
}
